import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  type InputSignal,
} from '@angular/core';
import { formatDate } from '@app/helpers/formatDateTime.helper';
import { formatUserName } from '@app/helpers/formatName.helper';
import { UserAccount } from '@app/models/account.model';
import { ProjectAdminResponse } from '@app/models/admin.model';
import type { FileInfo } from '@app/models/document.model';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { GenericStore } from '@app/stores/generic.store';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AttachmentListComponent } from '../attachment-list/attachment-list.component';

@Component({
  selector: 'app-project-details-layout',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AttachmentListComponent],
  templateUrl: './project-details-layout.component.html',
  styleUrl: './project-details-layout.component.scss',
  providers: [GetLocalizedPipe],
})
export class ProjectDetailsLayoutComponent {
  private readonly genericStore = inject(GenericStore);
  private readonly genericProjectStore = inject(GenericProjectStore);
  private readonly localized = inject(GetLocalizedPipe);
  private readonly translate = inject(TranslateService);

  isLoading: InputSignal<boolean> = input<boolean>(false);

  project: InputSignal<ProjectAdminResponse | null> =
    input<ProjectAdminResponse | null>(null);

  files = input.required<any[]>();

  contactUser = input<UserAccount | null>(null);
  readonly hasContactUser = computed(() => !!this.contactUser());
  readonly showContactCard = computed(
    () => !this.isLoading() && this.hasContactUser()
  );
  documentClick = output<FileInfo>();
  onDocClick(file: FileInfo): void {
    this.documentClick.emit(file);
  }

  stateLabel = (state?: number | null): string => {
    if (state == null) return '—';
    const statuses = this.genericProjectStore.projectStatusEntities();
    const name = statuses.find((status) => status.id === state)?.name?.trim();
    return name ? this.translate.instant(`generic.projectStatus.${name}`) : '—';
  };

  projectTypeLabel = (type?: number | null): string => {
    if (type == null) return '—';
    const types = this.genericProjectStore.projectFoundingEntities?.() ?? [];
    const name = types.find((t) => t.id === type)?.name?.trim();
    return name
      ? this.translate.instant(`generic.projectFounding.${name}`)
      : '—';
  };
  loanTermLabel = (term?: number | null): string => {
    if (term == null) return '—';
    const terms = this.genericProjectStore.projectLoanTermEntities?.() ?? [];
    const name = terms.find((t) => t.id === term)?.name?.trim();
    return name
      ? this.translate.instant(`generic.projectLoanTerm.${name}`)
      : '—';
  };

  contactName(): string {
    return formatUserName(this.contactUser());
  }

  readonly plannedPeriod = computed(() => {
    const startDate = this.project()?.loanTypePaymentStart ?? null;
    const endDate = this.project()?.loanTypePaymentEnd ?? null;
    return startDate && endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : '—';
  });

  readonly collectionPeriod = computed(() => {
    const startDate = this.project()?.startCollectionDate ?? null;
    const endDate = this.project()?.endCollectionDate ?? null;
    return startDate && endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : '—';
  });

  readonly documents = computed<FileInfo[]>(
    () => this.project()?.filesSectionA ?? []
  );

  getCountryName(code?: string | null): string {
    const value = (code ?? '').trim();
    if (!value) return '—';
    const entityValue = this.genericStore
      .countriesEntities()
      .find((country) => country.code === value);
    const name = this.localized.transform(entityValue?.localized);
    return name?.trim() || value;
  }
}
